/**
 * ScenarioViewModal — read-only view of a Test Scenario.
 * Design matches the reference image. No editing allowed.
 */
import React from 'react';
import './ScenarioViewModal.css';

interface ScenarioViewModalProps {
  scenario: {
    scenario_id: string;
    scenario_description: string;
    priority: string;
    tags?: string[];
    preconditions?: string;
    expected_result?: string;
    environment?: string;
    module_name?: string;
    created_by?: string;
    created_at?: string;
    version?: number;
  };
  onClose: () => void;
}

const PRIORITY_CONFIG: Record<string, { label: string; icon: React.ReactNode; color: string }> = {
  Critical: {
    label: 'Critical',
    color: '#dc2626',
    icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
  },
  High: {
    label: 'High',
    color: '#1e3a8a',
    icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="17 11 12 6 7 11"/><polyline points="17 18 12 13 7 18"/></svg>
  },
  Medium: {
    label: 'Medium',
    color: '#f59e0b',
    icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="13 6 19 12 13 18"/><polyline points="6 6 12 12 6 18"/></svg>
  },
  Low: {
    label: 'Low',
    color: '#22c55e',
    icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="4" y1="6" x2="20" y2="6"/><polyline points="8 12 12 16 16 12"/><line x1="4" y1="18" x2="20" y2="18"/></svg>
  },
};

const CLASSIFICATION_TAGS = ['Smoke', 'Sanity', 'Integration', 'Manual', 'API', 'UI', 'Performance', 'Security', 'Functional'];

export const ScenarioViewModal: React.FC<ScenarioViewModalProps> = ({ scenario, onClose }) => {
  const tags = scenario.tags || [];
  const classificationTags = tags.filter(t => CLASSIFICATION_TAGS.includes(t));
  const isRegression = tags.includes('Regression');
  const isAiGenerated = tags.includes('AI Generated');
  const priorities = ['Critical', 'High', 'Medium', 'Low'];

  return (
    <div className="svm-overlay" onClick={onClose}>
      <div className="svm-modal" onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="svm-header">
          <div>
            <div className="svm-id-label">SCENARIO IDENTIFIER</div>
            <div className="svm-id">{scenario.scenario_id}</div>
          </div>
          <button className="svm-close" onClick={onClose}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="svm-body">

          {/* Scenario Title */}
          <div className="svm-section">
            <div className="svm-section-label">SCENARIO TITLE</div>
            <div
              className="svm-title-box svm-rich-text"
              dangerouslySetInnerHTML={{ __html: scenario.scenario_description || '' }}
            />
          </div>

          {/* Priority Level */}
          <div className="svm-section">
            <div className="svm-section-label">PRIORITY LEVEL</div>
            <div className="svm-priority-cards">
              {priorities.map(p => {
                const cfg = PRIORITY_CONFIG[p] || PRIORITY_CONFIG.Medium;
                const isActive = scenario.priority === p;
                return (
                  <div
                    key={p}
                    className={`svm-priority-card ${isActive ? 'active' : ''}`}
                    style={isActive ? { borderColor: cfg.color, color: cfg.color } : {}}
                  >
                    <span className="svm-priority-icon" style={isActive ? { color: cfg.color } : {}}>
                      {cfg.icon}
                    </span>
                    <span className="svm-priority-label">{cfg.label}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Classification Tags */}
          <div className="svm-section">
            <div className="svm-section-label">CLASSIFICATION TAGS</div>
            <div className="svm-tags-row">
              {classificationTags.map(tag => (
                <span key={tag} className="svm-tag">
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor"><path d="M21.41 11.58l-9-9C12.05 2.22 11.55 2 11 2H4c-1.1 0-2 .9-2 2v7c0 .55.22 1.05.59 1.42l9 9c.36.36.86.58 1.41.58.55 0 1.05-.22 1.41-.59l7-7c.37-.36.59-.86.59-1.41 0-.55-.23-1.06-.59-1.42zM5.5 7C4.67 7 4 6.33 4 5.5S4.67 4 5.5 4 7 4.67 7 5.5 6.33 7 5.5 7z"/></svg>
                  {tag}
                </span>
              ))}
              {classificationTags.length === 0 && (
                <span className="svm-no-tags">No classification tags</span>
              )}
            </div>
          </div>

          {/* Regression + AI Generated toggles */}
          <div className="svm-toggles-row">
            <div className="svm-toggle-card">
              <div className="svm-toggle-info">
                <span className="svm-toggle-title">Regression Status</span>
                <span className="svm-toggle-sub">MANDATORY SUITE</span>
              </div>
              <div className={`svm-toggle ${isRegression ? 'on' : 'off'}`}>
                <div className="svm-toggle-thumb" />
              </div>
            </div>
            <div className="svm-toggle-card">
              <div className="svm-toggle-info">
                <span className="svm-toggle-title">AI Generated</span>
                <span className="svm-toggle-sub">MODEL VERIFIED</span>
              </div>
              <div className={`svm-toggle ${isAiGenerated ? 'on' : 'off'}`}>
                <div className="svm-toggle-thumb" />
              </div>
            </div>
          </div>

          {/* Extra fields if present */}
          {scenario.preconditions && (
            <div className="svm-section">
              <div className="svm-section-label">PRECONDITIONS</div>
              <div
                className="svm-text-box svm-rich-text"
                dangerouslySetInnerHTML={{ __html: scenario.preconditions }}
              />
            </div>
          )}
          {scenario.expected_result && (
            <div className="svm-section">
              <div className="svm-section-label">EXPECTED RESULT</div>
              <div
                className="svm-text-box svm-rich-text"
                dangerouslySetInnerHTML={{ __html: scenario.expected_result }}
              />
            </div>
          )}
          {scenario.environment && (
            <div className="svm-section">
              <div className="svm-section-label">ENVIRONMENT</div>
              <div className="svm-inline-value">{scenario.environment}</div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="svm-footer">
          <button className="svm-btn-close" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
};
